import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { templateService } from "../api/services/templateService";
import {
  TemplateFilters,
  TemplateResult,
  UpdateTemplatePayload,
  TemplateRequest,
} from "../schemas/templateSchema";

export const useTemplates = (
  filters: TemplateFilters = {},
  options?: { enabled?: boolean },
) => {
  const queryClient = useQueryClient();

  const {
    data: templates,
    isLoading: isTemplatesLoading,
    refetch,
  } = useQuery<TemplateResult, Error>({
    queryKey: ["templates", filters],
    queryFn: () => templateService.getAll(filters),
    enabled: options?.enabled !== false,
  });

  const createTemplateMutation = useMutation({
    mutationFn: templateService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
      toast.success("Template created successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create Template");
    },
  });

  const updateTemplateMutation = useMutation({
    mutationFn: templateService.update,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
      toast.success("Template updated successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update Template");
    },
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: templateService.remove,
    onSuccess: (_data, ids) => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
      if (Array.isArray(ids) && ids.length > 1) {
        toast.success(`${ids.length} items deleted successfully`);
        return;
      }
      toast.success("Template deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete Template");
    },
  });

  return {
    templates,
    refetchTemplates: refetch,
    isLoading:
      isTemplatesLoading ||
      createTemplateMutation.isPending ||
      updateTemplateMutation.isPending ||
      deleteTemplateMutation.isPending,
    createTemplate: createTemplateMutation.mutate,
    updateTemplate: updateTemplateMutation.mutate,
    deleteTemplates: deleteTemplateMutation.mutate,
  };
};
